import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkCell, CdkCellDef, CdkColumnDef, CdkFooterCell, CdkFooterCellDef, CdkFooterRow, CdkFooterRowDef, CdkHeaderCell, CdkHeaderCellDef, CdkHeaderRow, CdkHeaderRowDef, CdkRow, CdkRowDef, CdkTable } from '@angular/cdk/table';
import { AsyncPipe, LowerCasePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { L10n, L10nBase } from '@skylab-angular-shared/core';
import { CompanyContextLink, MatchPipe, TranslatePipe } from '@skylab-angular-shared/pipes';
import { MouseButton } from '@skylab-shared/ui';
import { isNumber, isUndefined, moveInArray } from '@skylab-shared/utils';
import { BehaviorSubject, Subject } from 'rxjs';
import { skip, take, takeUntil } from 'rxjs/operators';
import { Menu, MenuItem, MenuItemSeparator } from '../../../menu/menu.model';
import { MenuService } from '../../../menu/menu.service';
import { SortIconComponent } from '../../../sort-icon/sort-icon.component';
import { StarComponent } from '../../../star/star.component';
import { TooltipDirective } from '../../../tooltip/tooltip.directive';
import { OrderingDirection } from '../../../utils/ordering-direction';
import { ScreenSize } from '../../../utils/screen-size';
import { BooleanValueAccessorDirective } from '../../../value-accessors/boolean-value-accessor.directive';
import { ColumnDef, SelectionType, ServiceGridColumn, ServiceGridOptions } from '../../grid-ins.component';
import { GridExpandableRow } from '../../group/grid-expandable-row-utils';
import { GridGroupItem } from '../../group/grid-group-utils';
import { GridCellResizeDirective } from '../../resize/grid-cell-resize.directive';
import { ButtonComponent } from '../../../button/button.component';
import { ColumnSizes, GridResizableDirective, GridResizedEvent } from '../../resize/grid-resizable.directive';
import { HeaderResizerComponent } from '../../resize/header-resizer.component';
import { GridTemplateDirective } from '../../template/grid-template.directive';
import { IconComponent } from '../../../icon/icon.component';

@Component({
    selector: 'skl-table-view',
    templateUrl: 'grid-table-view.component.html',
    styleUrls: ['./grid-table-view.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [CompanyContextLink, CdkTable, GridResizableDirective, CdkDropList, NgClass, CdkColumnDef, CdkHeaderCellDef, CdkHeaderCell, CdkDrag, TooltipDirective, CdkDragHandle, NgTemplateOutlet, HeaderResizerComponent, SortIconComponent, CdkCellDef, CdkCell, GridCellResizeDirective, CdkFooterCellDef, CdkFooterCell, CdkHeaderRowDef, CdkHeaderRow, CdkRowDef, CdkRow, CdkFooterRowDef, CdkFooterRow, GridTemplateDirective, BooleanValueAccessorDirective, FormsModule, StarComponent, RouterLink, AsyncPipe, LowerCasePipe, MatchPipe, TranslatePipe, ButtonComponent, IconComponent]
})
export class GridTableViewComponent<TEntity, TColumnIdentity, TSortingIdentity> implements AfterViewInit, OnInit, OnDestroy {

    @Input('dataSource') public dataSource$!: BehaviorSubject<any>;

    @Input('columns') public columns$!: BehaviorSubject<ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[]>;

    @Input() public options!: ServiceGridOptions<TEntity, TColumnIdentity, TSortingIdentity>;

    @Input('customTemplates') public customTemplatesRefs = new QueryList<GridTemplateDirective<unknown>>();

    @Input('noDataRowTemplate') public noDataRowTemplate!: TemplateRef<any>;

    @Input() public displayHeaders = true;

    @ViewChildren(GridTemplateDirective) private baseTemplateRefs!: QueryList<GridTemplateDirective<unknown>>;

    public gridReady = false;
    public destroy$ = new Subject<void>();
    public displayedColumns$: BehaviorSubject<TColumnIdentity[]> = new BehaviorSubject<TColumnIdentity[]>([]);
    public columnSizes$: BehaviorSubject<ColumnSizes<TEntity>[]> = new BehaviorSubject<ColumnSizes<TEntity>[]>([]);
    private activeRow$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(0);
    private displayedColumnsState!: Map<TColumnIdentity, boolean>;

    public constructor(
        @Inject(L10n) protected readonly l10n: L10nBase,
        protected menuService: MenuService,
        protected changeDetection: ChangeDetectorRef,
        protected elementRef: ElementRef<HTMLElement>,
    ) { }

    public ngOnInit(): void {
        this.columns$.pipe(takeUntil(this.destroy$)).subscribe(columns => {
            this.initColumnsVisibilty(columns);
            this.initColumnsSizes(columns);
        });
        this.setGridApi();
    }

    public ngAfterViewInit(): void {
        this.gridReady = true;

        this.activeRow$.pipe(takeUntil(this.destroy$)).subscribe(index => {
            if (index !== null) {
                this.rowFocus(index);
            }
        });

        this.dataSource$.pipe(take(1), takeUntil(this.destroy$)).subscribe(elem => {
            if (!elem.length) return;

            const entity = this.options.api?.getInitialEntity?.()
                || this.dataSource$.getValue().find((elem: any) => !(elem as unknown as GridGroupItem<TEntity>).groupKey);

            if (this.isSelectionTable) this.initRowSelection(entity);
        });

        this.dataSource$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(elem => {
            if (!elem.length) return;
            this.initRowFocus(false, true);
        });

        this.options.selection?.changed.pipe(takeUntil(this.destroy$)).subscribe(_ => {
            const data = this.dataSource$.value;
            this.options.selection?.sort((ent1: TEntity, ent2: TEntity) => data.indexOf(ent1) - data.indexOf(ent2));
        });

        this.changeDetection.detectChanges();
    }

    public setGridApi(): void {
        if (!this.options.api) this.options.api = {};

        this.options.api.gridControl = {
            selection: this.selection,
            preventAutoFocus: false
        };

        this.options.api.displayedColumns$ = this.displayedColumns$;
    }

    public get stickyHeaderStyleClass(): string {
        return this.options.stickyHeader === false ? '' : 'row-sticky-top';
    }

    private initRowFocusIntervalHandler!: ReturnType<typeof setInterval>;
    public initRowFocus(init = false, preventScroll = false): void {
        if (this.options.api?.gridControl?.preventAutoFocus || this.options.viewType === 'table-virtual') {
            clearInterval(this.initRowFocusIntervalHandler);
            return;
        }

        clearInterval(this.initRowFocusIntervalHandler);
        this.initRowFocusIntervalHandler = setInterval(() => {
            const activeVal = this.activeRow$.value;
            if (activeVal !== null) {
                const elm = this.rowFocus(activeVal);
                if (elm) {
                    if ((activeVal === 0) && !init && !preventScroll) this.elementRef.nativeElement.scrollIntoView({ block: 'nearest' });
                    clearInterval(this.initRowFocusIntervalHandler);
                } else {
                    this.initRowFocus(init, preventScroll);
                }
            } else {
                clearInterval(this.initRowFocusIntervalHandler);
            }
        }, 50);
    }

    private initRowSelection(entity: TEntity): void {
        const index = this.dataSource$.getValue().findIndex((elm: any) => elm === entity);
        this.selection?.single(entity);
        this.activeRow$.next(index);
        this.initRowFocus(true);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        clearInterval(this.initRowFocusIntervalHandler);
    }

    public isSortable(sortBy: TSortingIdentity): boolean {
        if (!sortBy || !this.options?.sorting) { return false; }
        return this.options.sorting.key === sortBy;
    }

    public handleHeaderClick(event: MouseEvent, column: ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>): void {
        event.stopPropagation();
        this.handleSort(column.identityName);
    }

    public handleHeaderContextMenu(event: MouseEvent): void {
        if (!this.headerMenu) return;

        event.preventDefault();
        this.menuService.createMenu(this.headerMenu.items, event);
    }

    public handleRowContextMenu(event: MouseEvent, entity: TEntity): void {
        if (!this.isSelectionTable || (event?.target as HTMLAnchorElement)?.href) {
            return;
        }

        event.preventDefault();

        if (!this.options?.contextMenu) return;

        if (!this.selection?.isSelected(entity)) {
            this.selection?.single(entity);
        }
        this.menuService.createMenu(this.options.contextMenu.items, event, { context: entity });
    }

    public handleRowDblClick(event: Event, entity: TEntity): void {
        event.stopPropagation();
        this.options?.api?.mouseControl?.rowDblClick?.(event, entity);
    }

    public handleRowSingleClick(event: MouseEvent, entity: TEntity): void {
        event.stopPropagation();
        if (!this.isSelectionTable) return;

        const isShiftPressed = event.shiftKey;
        const isCtrlPressed = event.ctrlKey;
        const isMultiSelection = this.selection?.isMultipleSelection();

        if (isMultiSelection && isCtrlPressed) {
            this.selection?.toggle(entity);
        } else if (isMultiSelection && isShiftPressed) {
            this.selection?.range(entity);
        } else {
            this.selection?.single(entity);
        }

        this.options?.api?.mouseControl?.rowClick?.(event, entity);
    }

    public handleRowMiddleClick(event: MouseEvent, entity: TEntity): void {
        if (event.button === MouseButton.Middle) {
            this.options?.api?.mouseControl?.rowMiddleClick?.(event, entity);
        }
    }

    public rowSelection(event: MouseEvent, entity: TEntity): void {
        event.stopPropagation();
        const isShiftPressed = event.shiftKey;
        const isMultiSelection = this.selection?.isMultipleSelection();
        isMultiSelection && isShiftPressed ? this.selection?.range(entity) : this.selection?.toggle(entity);
    }

    public headerSelection(event: MouseEvent): void {
        event.stopPropagation();
        const isShiftPressed = event.shiftKey;
        if (isShiftPressed) {
            this.selection?.inverse();

            return;
        }
        this.selection?.allToggle();
    }

    public setActiveRow(entity: TEntity): void {
        const index = this.dataSource$.value.findIndex((elem: any) => elem === entity);
        this.activeRow$.next(index);
    }

    public handleSort(identityName: TColumnIdentity): void {
        const sortBy = this.findColumnByIdentityName(identityName)?.sortingBy;
        if (!sortBy || !this.options?.sorting) return;

        const oldDirection = this.options.sorting.direction;
        const sortingValue = {
            direction: oldDirection === OrderingDirection.Asc ? OrderingDirection.Desc : OrderingDirection.Asc,
            key: sortBy
        };

        this.options.sorting = sortingValue;
        this.options.api?.onSortingChange?.({...sortingValue, key: identityName});
    }

    public handleDragAndDrop(event: CdkDragDrop<TColumnIdentity[], any>): void {
        if (event) {
            if (event.previousIndex === event.currentIndex) return;
            const currentIdentity = this.displayedColumns$.value.find((elem, index) => index === event.currentIndex);
            const previousIdentity = this.displayedColumns$.value.find((elem, index) => index === event.previousIndex);
            if (currentIdentity === undefined || previousIdentity === undefined) return;
            this.displayedColumns$.next(moveInArray(this.displayedColumns$.value, event.previousIndex, event.currentIndex));

            const arr = [...this.displayedColumnsState.entries()];
            const columnsStateCurrentIndex = arr.findIndex(elem => elem[0] === currentIdentity);
            const columnsStatePreviousIndex = arr.findIndex(elem => elem[0] === previousIdentity);
            const toMove = arr.splice(columnsStatePreviousIndex, 1)[0];
            arr.splice(columnsStateCurrentIndex, 0, toMove);

            this.displayedColumnsState = new Map(arr);

            this.options.api?.onDisplayedColumnChange?.(this.displayedColumnsState);
        }
    }

    public handleColumnResized(event: GridResizedEvent<TEntity>): void {
        if (event && this.options?.columnDividerTouched) {
            const { columnDef, columnWidth } = event;
            const newColumns = this.updateColumn(columnDef, { width: columnWidth });

            this.options.columnDividerTouched[`${ ScreenSize.getDeviceWidth() }`] = true;
            this.initColumnsSizes(this.columns$.value);
            const foundCol = newColumns.find(elem => elem.columnDef === columnDef);
            if (foundCol) {
                this.options.api?.onColumnResized?.(event, foundCol.identityName);
            }
            this.changeDetection.markForCheck();
        }
    }

    public sortPredicate(index: number): boolean {
        const col = this.columns$.value[index];

        return !this.isDragDisabled(col?.isMovable);
    }

    public getRow(columnDef: string): TEntity {
        return this.dataSource$.value[columnDef];
    }

    public getTemplate(templateName: string): TemplateRef<any> {
        const templates = [...this.customTemplatesRefs?.toArray(), ...this.baseTemplateRefs.toArray()];
        const template = templates.find(x => x.name?.toLowerCase() == templateName.toLowerCase())?.template;
        return template!;
    }

    public isDragDisabled(isMovable: boolean | undefined): boolean {
        if (isMovable === undefined) {
            return false;
        }
        return !isMovable;
    }

    public isRowActive(index: number): boolean {
        return this.activeRow$.value === index;
    }

    public isFillingColumnActive(fillingColumn: boolean): boolean {
        if (typeof this.options?.columnDividerTouched === 'object' && fillingColumn) {
            return !this.options.columnDividerTouched[`${ ScreenSize.getDeviceWidth() }`];
        }

        return fillingColumn;
    }

    protected get selection(): SelectionType<TEntity> {
        const getEntities = () => this.dataSource$.value.filter((elem: any) => !this.isExpandableRow(null as any, elem));

        return {
            clear: () => {
                this.options.selection?.clear();
                this.activeRow$.next(null);
            },
            single: (entity: TEntity) => {
                if (this.selection?.isMultipleSelection()) {
                    this.options.selection?.clear();
                }
                this.options.selection?.select(entity);
                this.setActiveRow(entity);
                this.changeDetection.detectChanges();
            },
            toggle: (entity: TEntity) => {
                if (!this.options.selection) return;

                this.options.selection?.toggle(entity);
                this.setActiveRow(entity);
            },
            multi: (entities: TEntity[]) => {
                this.options.selection?.clear();
                this.options.selection?.select(...entities);
            },
            sibling: () => {
                const currentLength = getEntities().length;
                const currentActiveIndex = this.activeRow$.value;

                if (!currentLength || currentActiveIndex === null) return;

                let nextActiveIndex: number;

                if (currentLength === 1) {
                    nextActiveIndex = 0;
                } else if (currentLength <= currentActiveIndex + 1) {
                    nextActiveIndex = currentLength - 1;
                } else {
                    nextActiveIndex = currentActiveIndex;
                }

                this.selection?.single(getEntities()[nextActiveIndex]);
            },
            range: (entity: TEntity) => {
                const idx = getEntities().indexOf(entity);
                const range = getEntities().map((entity: any, index: number) => ({ index, entity })).filter((item: any) => this.options.selection?.isSelected(item.entity));
                const lastItem = range[range.length - 1];
                const lastItemIndex = lastItem?.index ?? idx;

                if (idx === lastItemIndex) {
                    this.selection?.toggle(entity);

                    return;
                }
                const selectedItems = idx > lastItemIndex ? getEntities().slice(lastItemIndex, idx + 1) : getEntities().slice(idx, lastItemIndex);
                this.options.selection?.select(...selectedItems);
                this.setActiveRow(entity);
            },
            allToggle: () => {
                this.selection?.isAllSelected() ? this.options.selection?.clear() : this.options.selection?.select(...getEntities());
            },
            inverse: () => {
                const inverse = getEntities().filter((entity: any) => !this.options.selection?.isSelected(entity));
                this.options.selection?.clear();
                this.options.selection?.select(...inverse);
            },
            selected: () => {
                return this.options.selection?.selected;
            },
            isSelected: (entity: TEntity) => {
                return this.options.selection?.isSelected(entity);
            },
            isAllSelected: () => {
                return getEntities().length === this.options.selection?.selected.length && !(getEntities().length <= 0);
            },
            isIndeterminate: () => {
                return !this.selection?.isAllSelected() && !this.options.selection?.isEmpty();
            },
            isMultipleSelection: () => {
                return this.options.selection?.isMultipleSelection();
            }
        };
    }

    public get isSelectionTable(): boolean {
        return !!this.options?.selection;
    }

    public get isFooterVisible(): boolean {
        return this.options?.summary?.isVisible ?? false;
    }

    public get isHeaderVisible(): boolean {
        return this.columns$.value.some(column => column.header);
    }

    public get noDataCheck(): boolean {
        return !this.dataSource$.value.length;
    }

    public get columnMapper(): (keyof TEntity)[] {
        return this.displayedColumns$.value.map(dispColumn => this.columns$.value.find(column => column.identityName === dispColumn)?.columnDef ?? dispColumn as any);
    }

    private get headerMenu(): Menu | undefined {
        const gridDefaultOptions = this.options.gridDefaultOptions;
        if (!this.options.disabledToToggle && !gridDefaultOptions) return;

        const defaultOpts = gridDefaultOptions?.();
        if (!defaultOpts) return;

        const items = defaultOpts.columns.value
            .map((elem: any) => elem.identityName)
            .filter((elem: any) => !this.options.disabledToToggle?.includes(elem));

        const columnMenu = items.map((identityName: any) => {
            const column = this.findColumnByIdentityName(identityName);
            return new MenuItem({
                label: column?.label ?? column?.header ?? '',
                getIcon: () => {
                    return this.displayedColumnsState.get(identityName) ? 'icon-check-square' : 'icon-square';
                },
                click: () => this.toggleColumn(identityName),
                closeAfterClick: false
            });
        });

        const defaultColumns = new MenuItem({
            label: this.l10n._T('ui.grid.default-options'),
            click: () => {
                const defaults = gridDefaultOptions?.();
                if (!defaults) return;
                this.columns$.next(defaults.columns.value);
                this.options.sorting = defaults.sorting;
                this.options.summary = defaults.summary;
                this.options.columnDividerTouched = defaults.columnDividerTouched;
                this.initColumnsVisibilty(this.columns$.value);
                this.initColumnsSizes(this.columns$.value);
                this.options.api?.onGridDefaultTriggered?.();
            },
        });

        const summaryItem = new MenuItem({
            label: this.l10n._T('ui.grid.summary'),
            click: () => {
                if (this.options.summary) {
                    this.options.summary.isVisible = !this.options.summary.isVisible;
                    this.options.api?.onSummaryChanged?.();
                }
            },
            getIcon: () => {
                return this.options.summary?.isVisible ? 'icon-check-square' : 'icon-square';
            },
            closeAfterClick: false
        });

        return new Menu([
            ...columnMenu,
            ...(this.options.summary ? [new MenuItemSeparator(), summaryItem] : []),
            new MenuItemSeparator(),
            defaultColumns
        ]);
    }

    private rowFocus(index: number): HTMLElement | null | undefined {
        if (!isNumber(index) || this.options.viewType === 'table-virtual') return;

        const elm = this.elementRef.nativeElement.querySelector(`#row-${ index + 1 }`) as HTMLElement | null;
        elm?.focus();

        return elm;
    }

    private isColumnVisible(column: ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>): boolean {
        if (column.isForcedHidden?.()) return false;
        return !column?.isHidden;
    }

    private initColumnsVisibilty(columns: ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[]): void {
        const displayColumns = columns.filter(col => this.isColumnVisible(col)).map(column => column.identityName);
        this.displayedColumns$.next(displayColumns);
        this.displayedColumnsState = new Map(columns.map(column => [column.identityName, this.isColumnVisible(column)]));
    }

    private initColumnsSizes(columns: ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[]): void {
        const columnSizes = columns.map(({ width, minWidth, fillingColumn, columnDef }) => {
            return {
                columnDef,
                width,
                minWidth,
                fillingColumn: this.isFillingColumnActive(!!fillingColumn)
            } satisfies ColumnSizes<TEntity>;
        });
        this.columnSizes$.next(columnSizes);
    }

    private toggleColumn(key?: TColumnIdentity | undefined): void {
        if (key === undefined) {
            this.initColumnsVisibilty(this.columns$.value);

            return;
        }

        if (this.displayedColumnsState.has(key)) {
            const toggleValue = !this.displayedColumnsState.get(key);
            this.displayedColumnsState.set(key, toggleValue);

            const column = this.findColumnByIdentityName(key);
            if (column) {
                column.isHidden = !toggleValue;
            }

            const visibleColumns = [...this.displayedColumnsState.entries()].filter(val => val[1]).flatMap(val => val[0]);
            this.displayedColumns$.next(visibleColumns);
            this.options.api?.onDisplayedColumnChange?.(this.displayedColumnsState);
            this.columns$.next([...this.columns$.value]);
        }
    }

    private findColumnByIdentityName(identityName: TColumnIdentity): ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any> | undefined {
        return this.columns$.value.find(column => column.identityName === identityName);
    }

    private updateColumn<TCustomField>(columnDef: ColumnDef<TEntity, TCustomField>, updateProps: Partial<ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>>): ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[] {
        const newColumns = [...this.columns$.value];
        const updatedColumn = newColumns.find(column => column.columnDef === columnDef);
        if (updatedColumn) {
            Object.keys(updateProps).forEach(key => {
                (updatedColumn as any)[key] = (updateProps as any)[key];
            });
        }

        return newColumns;
    }

    private get keyboardAction() {
        const activeVal = this.activeRow$.value;
        const entity = activeVal !== null ? this.dataSource$.value[activeVal] : null;
        return {
            nextItem: () => {
                let currentValue = this.activeRow$.value ?? 0;
                const nextIndex = isNumber(currentValue) ? (currentValue >= this.dataSource$.value.length - 1 ? this.dataSource$.value.length - 1 : ++currentValue) : 0;
                this.activeRow$.next(nextIndex);
            },
            prevItem: () => {
                let currentValue = this.activeRow$.value ?? 0;
                const prevIndex = currentValue <= 0 ? 0 : --currentValue;
                this.activeRow$.next(prevIndex);
            },
            selectItem: () => {
                if (entity) this.selection?.toggle(entity);
            },
            selectRangeItems: () => {
                if (entity) this.selection?.range(entity);
            },
            action: (event: Event) => {
                if (entity) this.handleRowDblClick(event, entity);
            }
        };
    }

    public getDef(column: ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>): string {
        return column.columnDef as string;
    }

    @HostListener('keydown', ['$event'])
    protected handleKeyDownEvent(event: KeyboardEvent): void {
        const keyboard = this.keyboardAction;

        if (event.code === 'ArrowUp') {
            event.preventDefault();
            keyboard.prevItem();
            this.options.api?.keyboardControl?.onArrowUp?.(event);
        }

        if (event.code === 'ArrowDown') {
            event.preventDefault();
            keyboard.nextItem();
            this.options.api?.keyboardControl?.onArrowDown?.(event);
        }

        if (event.code === 'Tab') {
            event.shiftKey ? keyboard.prevItem() : keyboard.nextItem();
            this.options.api?.keyboardControl?.onTab?.(event);
        }

        if (event.code === 'Space') {
            event.shiftKey ? keyboard.selectRangeItems() : keyboard.selectItem();
            this.options.api?.keyboardControl?.onSpace?.(event);
        }

        if (event.code === 'Enter') {
            event.preventDefault();
            keyboard.selectItem();
            keyboard.action(event);
            this.options.api?.keyboardControl?.onEnter?.(event);
        }

        if (event.code === 'Delete') {
            this.options.api?.keyboardControl?.onDelete?.(event);
        }
    }

    //#region groups
    public isGroupItem(_: any, group: GridGroupItem<TEntity>): boolean {
        return !!group.groupValue;
    }

    public toggleGroup(group: GridGroupItem<TEntity>): void {
        group.expanded = !group.expanded;
    }

    public isGroupEntityHidden(entity: TEntity): boolean {
        const groups = this.groupItems;

        if (!groups.length) return false;
        return !groups.find(elem => elem.groupValue === (entity as any)[elem.groupKey])?.expanded;
    }

    public isRowItalicStyled(entity: TEntity): boolean {
        return this.options?.isRowItalicStyled?.(entity) ?? false;
    }

    private get groupItems(): GridGroupItem<TEntity>[] {
        const items = this.dataSource$.value as unknown as GridGroupItem<TEntity>[];
        return items.filter(elem => elem.groupKey);
    }

    public get isGroupMode(): boolean {
        return !!this.dataSource$.value.find((elem: any) => (elem as unknown as GridGroupItem<TEntity>).groupKey);
    }
    //#endregion

    //#region extendable row
    public isExpandableRow(_: any, expandableRow: GridExpandableRow<TEntity>): boolean {
        return !!expandableRow.expandableEntityKey;
    }

    public isExpandableRowHidden(entity: GridExpandableRow<TEntity> & TEntity): boolean {
        return isUndefined(entity.expanded) ? false : !entity.expanded;
    }

    public toggleExpandableRow(entity: TEntity): void {
        const expandableIdx = this.dataSource$.value.findIndex((elem: any) => elem === entity) + 1;
        this.dataSource$.value[expandableIdx].expanded = !this.dataSource$.value[expandableIdx].expanded;
    }

    public isRowExpanded(entity: TEntity): boolean {
        const expandableIdx = this.dataSource$.value.findIndex((elem: any) => elem === entity) + 1;
        return this.dataSource$.value[expandableIdx].expanded;
    }

    public getExpandableRowEntity(entity: GridExpandableRow<TEntity>): TEntity {
        const parentIdx = this.dataSource$.value.findIndex((elem: any) => elem === entity) - 1;
        return this.dataSource$.value[parentIdx];
    }
    //#endregion
}
